import React, { useState } from 'react';
import { X, Lock, AlertTriangle, Zap, Wifi, CheckCircle, Loader2 } from 'lucide-react';
import { apiFetch } from '../api';

export default function ModeSwitchModal({
  isOpen,
  onClose,
  currentMode,
  targetMode,
  onConfirm,
  email
}) {
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const isSocketSwitch = targetMode === 'socket';
  const isSameMode = currentMode === targetMode;

  const handleSubmit = async (e) => {
    e.preventDefault();

    // If clicking on the current mode, switch to the opposite mode
    if (isSameMode) {
      const newMode = currentMode === 'socket' ? 'api' : 'socket';

      // If switching from Socket to API, no password needed
      if (newMode === 'api') {
        onConfirm(newMode);
        onClose();
        return;
      }

      // If switching from API to Socket, need password
      if (!password.trim()) {
        setError('Please enter the enable password');
        return;
      }

      setIsLoading(true);
      setError('');

      try {
        const response = await apiFetch(`/active/enable/socket?code=${password}`, {
          method: 'GET'
        });

        const data = await response.json();

        if (data.STATUS === 'SUCCESS') {
          onConfirm(newMode);
          setPassword('');
          onClose();
        } else {
          setError(data.MESSAGE || 'Invalid password. Please try again.');
        }
      } catch (err) {
        console.error('Password validation error:', err);
        setError('Failed to validate password. Please try again.');
      } finally {
        setIsLoading(false);
      }

      return;
    }

    // If switching to API mode, no password needed
    if (targetMode === 'api') {
      onConfirm(targetMode);
      onClose();
      return;
    }

    // If switching to socket mode, validate password
    if (!password.trim()) {
      setError('Please enter the enable password');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const response = await apiFetch(`/active/enable/socket?code=${password}`, {
        method: 'GET'
      });

      const data = await response.json();

      if (data.STATUS === 'SUCCESS') {
        onConfirm(targetMode);
        setPassword('');
        onClose();
      } else {
        setError(data.MESSAGE || 'Invalid password. Please try again.');
      }
    } catch (err) {
      console.error('Password validation error:', err);
      setError('Failed to validate password. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setPassword('');
    setError('');
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="relative bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl shadow-2xl border border-white/10 max-w-md w-full overflow-hidden">
        {/* Animated gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 via-transparent to-blue-500/10 opacity-50" />

        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-white/60 hover:text-white transition-colors z-10"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="relative p-6">
          {/* Icon */}
          <div className="flex justify-center mb-4">
            {isSameMode ? (
              <div className="w-16 h-16 bg-yellow-500/20 rounded-full flex items-center justify-center border border-yellow-500/30">
                <AlertTriangle className="w-8 h-8 text-yellow-400" />
              </div>
            ) : isSocketSwitch ? (
              <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-500 rounded-full flex items-center justify-center shadow-lg">
                <Wifi className="w-8 h-8 text-white" />
              </div>
            ) : (
              <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-full flex items-center justify-center shadow-lg">
                <Zap className="w-8 h-8 text-white" />
              </div>
            )}
          </div>

          {/* Title & Description */}
          <div className="text-center mb-6">
            {isSameMode ? (
              <>
                <h3 className="text-xl font-bold text-white mb-2">
                  Already in {currentMode === 'socket' ? 'Socket' : 'API'} Mode
                </h3>
                <p className="text-white/60 text-sm">
                  You are currently using {currentMode === 'socket' ? 'Socket-based' : 'API-based'} communication.
                  {currentMode === 'socket'
                    ? ' Would you like to switch to API mode?'
                    : ' Would you like to switch to Socket mode?'}
                </p>
              </>
            ) : isSocketSwitch ? (
              <>
                <h3 className="text-xl font-bold text-white mb-2">
                  Enable Socket Mode
                </h3>
                <p className="text-white/60 text-sm">
                  Socket mode provides real-time communication. Please enter the enable password to activate.
                </p>
              </>
            ) : (
              <>
                <h3 className="text-xl font-bold text-white mb-2">
                  Switch to API Mode
                </h3>
                <p className="text-white/60 text-sm">
                  Are you sure you want to switch to API-based communication?
                </p>
              </>
            )}
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Password input - show when switching TO socket mode */}
            {((isSocketSwitch && !isSameMode) || (isSameMode && currentMode === 'api')) && (
              <div>
                <label className="block text-sm font-medium text-white/80 mb-2">
                  Enable Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-white/40" />
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      setError('');
                    }}
                    placeholder="Enter password"
                    className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-green-500/50 focus:border-green-500/50 transition-all"
                    disabled={isLoading}
                  />
                </div>
              </div>
            )}

            {/* Error message */}
            {error && (
              <div className="flex items-start space-x-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-red-200">{error}</span>
              </div>
            )}

            {/* Current mode info */}
            {!isSameMode && (
              <div className="flex items-start space-x-2 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                <CheckCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-blue-200">
                  <p className="font-medium">Currently using {currentMode === 'socket' ? 'Socket' : 'API'} mode</p>
                  <p className="text-blue-300/80 mt-1">
                    Switching to {targetMode === 'socket' ? 'Socket' : 'API'} mode will change how data is transmitted.
                  </p>
                </div>
              </div>
            )}

            {/* Buttons */}
            <div className="flex space-x-3 pt-2">
              <button
                type="button"
                onClick={handleClose}
                disabled={isLoading}
                className="flex-1 px-4 py-3 bg-white/5 hover:bg-white/10 text-white rounded-xl font-medium transition-all duration-200 border border-white/10 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isLoading || ((isSocketSwitch && !isSameMode) || (isSameMode && currentMode === 'api')) && !password.trim()}
                className={`flex-1 px-4 py-3 rounded-xl font-medium transition-all duration-200 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed ${isSameMode
                  ? (currentMode === 'socket'
                    ? 'bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white shadow-lg'
                    : 'bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white shadow-lg')
                  : isSocketSwitch
                    ? 'bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white shadow-lg'
                    : 'bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white shadow-lg'
                  }`}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>Validating...</span>
                  </>
                ) : isSameMode ? (
                  <span>Switch to {currentMode === 'socket' ? 'API' : 'Socket'} Mode</span>
                ) : (
                  <span>Confirm Switch</span>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}